import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import CTAButton from 'src/components/common/CTAButtonNew'
import { selectCTAParams } from 'src/modules/reducers/params'
import { defaultCTAURI } from 'src/modules/utils/constants'
import { createUrl } from 'src/modules/utils/createUrl'
import { replaceUrlParam } from 'src/modules/utils/replaceUrlParam'
import styled from 'styled-components'
import Image from '../Image'
import { fireUIInteractionEvent } from 'src/modules/analytics/v3'
import { selectStateForProtobuf } from 'src/modules/reducers'
import {
  body,
  borderRadius,
  colors,
  heading,
  spacing,
  typographyMixin,
} from 'src/constants/marketing-ds'
import { Checkmark } from 'src/components/icons/CheckMark'
import { CrossMark } from 'src/components/icons/CrossMark'

const PlanCard = ({
  offerCode,
  plan,
  title,
  subtitle,
  price,
  discountedPrice,
  tagline,
  listItems,
  ctaText,
  footnote,
  promoImg,
  lang = 'en',
}) => {
  const parsedParams = useSelector(selectCTAParams)
  let ctaURL = createUrl(defaultCTAURI, parsedParams)
  const stateForProtobuf = useSelector(selectStateForProtobuf)

  ctaURL = replaceUrlParam(ctaURL, 'offer', offerCode)
  ctaURL = replaceUrlParam(ctaURL, 'comp', 'plan-card')

  const monthLang = lang === 'es' ? 'mes' : 'mo'

  //REMOVED: univision_removed promoImg
  // const promoImg =
  //   'https://assets-imgx.fubo.tv/marketing/lp/images/card/latino/LA_PlanCardRefresh_8-14.png?ch=width&auto=format,compress'

  //REPLACED: univision_removed promoImg
  const promoImage = promoImg
    ? promoImg
    : 'https://assets-imgx.fubo.tv/marketing/lp/images/card/latino/LA_LP_LogoTiles_03-2025.png?ch=width&auto=format,compress'

  const [imageWidth, setImageWidth] = useState(0)
  const [cardWidth, setCardWidth] = useState(0)
  const imageRef = React.useRef(null)
  const cardRef = React.useRef(null)

  const handleImage = (event) => {
    // Capture the width of the image
    const width = event.target.offsetWidth
    // Set the container width to 2/3 of the image width
    setImageWidth((2 / 3) * width)
  }

  useEffect(() => {
    if (imageRef.current) {
      // If the image has already loaded, capture the width
      if (imageRef.current.complete) {
        handleImage({ target: imageRef.current })
      }
    }
  }, [imageRef])

  useEffect(() => {
    if (cardRef.current) {
      setCardWidth(cardRef.current.offsetWidth)
    }
  }, [cardRef])

  useEffect(() => {
    function handleResize() {
      if (imageRef.current) {
        // If the image has already loaded, capture the width
        if (imageRef.current.complete) {
          handleImage({ target: imageRef.current })
        }
      }
      if (cardRef.current) {
        setCardWidth(cardRef.current.offsetWidth)
      }
    }

    // Attach the event listener to the window object
    window.addEventListener('resize', handleResize)

    // Remove the event listener when the component unmounts
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  const handleOnClick = () => {
    fireUIInteractionEvent(
      {
        element: offerCode,
        component: 'plan_card_cta_clicked',
        type: 'clicked',
      },
      stateForProtobuf
    )
  }

  return (
    <StyledPlanCard imageWidth={imageWidth} cardWidth={cardWidth}>
      <a className="planCard" href={ctaURL} onClick={handleOnClick}>
        <div className="planCard__main" ref={cardRef}>
          <div className="planCard__content">
            <div className="planCard__header">
              <div className="planCard__title">{title}</div>
              <div className="planCard__subtitle">{subtitle}</div>
              <div className="planCard__divider"></div>
              <div className={`planCard__price`}>
                {discountedPrice ? (
                  <>
                    ${discountedPrice}/{monthLang}
                    {footnote && '*'}{' '}
                    <span className="faded-price">
                      ${price}/{monthLang}
                    </span>
                  </>
                ) : (
                  <>
                    ${price}/{monthLang}
                    {footnote && '*'}
                  </>
                )}
              </div>
              {tagline && <div className="discount__note">{tagline}</div>}
            </div>
            <PlanCardList className="planCard__list">
              {listItems?.map((item, index) => {
                if (item === null) return null
                const check =
                  item?.available === undefined ? (
                    <span style={{ height: '20px' }}> </span>
                  ) : item?.available ? (
                    <Icon type="check">
                      <Checkmark />
                    </Icon>
                  ) : (
                    <Icon type="cross">
                      <CrossMark />
                    </Icon>
                  )
                return (
                  <ListItem key={index} className="list-item">
                    {check} {item.text}
                  </ListItem>
                )
              })}
            </PlanCardList>
          </div>
          <div className="planCard_cta_container">
            <div className="planCard_cta">
              <CTAButton link={ctaURL}>{ctaText}</CTAButton>
            </div>
          </div>
        </div>
        {promoImage && (
          <>
            <div className="planCard__promoImg__container">
              <div class="triangle"></div>
              <div className="planCard__promoImg">
                <Image src={promoImage} onLoad={handleImage} ref={imageRef} />
              </div>
            </div>
          </>
        )}
      </a>
      {footnote && <div className="planCard__footnote">{footnote}</div>}
    </StyledPlanCard>
  )
}

export default PlanCard

const cardBreakpoint = 992
const desktopBreak = cardBreakpoint + 'px'
const mobileBreak = cardBreakpoint - 1 + 'px'

const StyledPlanCard = styled.div`
  position: relative;
  .planCard {
    margin: 0 auto;
    color: #424242;
    display: flex;
    flex-direction: row;
    position: relative;
    border-radius: ${borderRadius.md};
    overflow: hidden;
    box-sizing: border-box;
    background: #fff;
    cursor: pointer;
    transition: transform 0.4s;
    @media (max-width: ${mobileBreak}) {
      flex-direction: column;
      align-items: center;
      max-width: 400px;
      .triangle {
        display: none;
      }
    }
    .planCard__main {
      position: relative;
      width: 100%;
      max-width: 400px;
      box-sizing: border-box;
      flex: 0 0 400px;
      padding: ${spacing.lg} ${spacing.md} ${spacing.md};
      z-index: 3;
      background: #fff;
      @media (min-width: ${desktopBreak}) {
        padding: ${spacing.xl} ${spacing.lg};
        width: 470px;
        max-width: 470px;
        flex: 0 0 470px;
      }
    }
    .triangle {
      position: absolute;
      background: #fff;
      content: '';
      ${'' /* width: 120px; */}
      aspect-ratio: 1 / 7;
      height: 120%;
      left: 0;
      bottom: 0;
      margin-left: auto;
      margin-right: auto;
      flex-shrink: 0;
      transform: translateX(-55%) translateY(10%) rotate(9deg);
      z-index: 2;
      box-shadow: 0 0 70px 30px rgba(0, 0, 0, 0.5);
      @media (max-width: ${mobileBreak}) {
        .triangle {
          display: none;
        }
      }
    }

    .planCard__content {
      display: flex;
      flex-direction: column;
      position: relative;
      z-index: 3;
      flex: 1;
      color: ${colors.black};

      .planCard__header {
        display: flex;
        flex-direction: column;
        gap: ${spacing.lg};
        .planCard__title {
          ${typographyMixin(heading.lg)}
        }
        .planCard__subtitle {
          ${typographyMixin(body.lg)}
        }
        .planCard__divider {
          background: ${colors.slate[100]};
          height: 1px;
          width: 64px;
        }
        .planCard__price {
          display: flex;
          align-items: center;
          gap: ${spacing.xxs};
          flex-wrap: wrap;
          ${typographyMixin(heading.md)}
        }
        .faded-price {
          ${typographyMixin(body.lg)}
          color: ${colors.slate[100]};
          text-decoration: line-through;
          -webkit-text-decoration-color: ${colors.red[120]};
          text-decoration-color: ${colors.red[120]};
        }
        .discount__note {
          ${typographyMixin(heading.xs)}
          text-decoration: none;
          text-transform: none;
          color: ${colors.slate[10]};
          display: inline-block;
          background-color: ${colors.mint[120]};
          border-radius: ${borderRadius.xs};

          span {
            display: inline-block;
            padding: ${spacing.xxxs};
          }
        }
      }
    }
    .planCard_cta_container {
      position: relative;
      z-index: 3;
    }
    .planCard_cta {
      position: relative;
      z-index: 2;
      margin-right: auto;
    }
  }
  .planCard__footnote {
    ${typographyMixin(body.xs)}
    text-align: center;
    margin-top: ${spacing.sm};
  }

  .planCard__promoImg__container {
    background: ${colors.gradients.slate.light};
    width: 100%;
    @media (min-width: ${desktopBreak}) {
      justify-content: center;
      flex: 0 0 ${(props) => props.imageWidth + 'px' || '0'};
      overflow: hidden;
      position: relative;
    }
  }

  .planCard__promoImg {
    width: ${(props) => props.cardWidth + 'px' || '0'};
    height: ${(props) => (props.cardWidth * 855) / 705 + 'px' || '0'};
    @media (min-width: ${desktopBreak}) {
      height: 100%;
      width: ${(props) => props.imageWidth + 'px' || '0'};
    }
    img {
      transform: translateX(-19.5%) scale(1.05);
      height: 100%;
      @media (min-width: ${desktopBreak}) {
        position: absolute;
      }
    }
  }
`

const Icon = styled.span`
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 1px;
  color: ${({ type }) => (type === 'check' ? colors.mint[80] : colors.red[120])};
`

// List of features/benefits
const PlanCardList = styled.div`
  display: flex;
  flex-direction: column;
  align-self: stretch;
  gap: ${spacing.sm}; // 16px gap from Figma
  margin: ${spacing.lg} 0 ${spacing.xl};
`
// Individual list item
const ListItem = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  ${typographyMixin(body.md)}
  color: ${colors.black};
  gap: ${spacing.xxxs};
`
